// vue
import { ref } from '@vue/composition-api';

// mapbox
import mapboxgl from 'mapbox-gl';

// store
import { useStore } from '@/store';

// services
import { useService } from '@/vue3/api/services';

// hooks
import { useNotify } from '@/vue3/hooks/notifyHook';
import { useGeoRecord } from '@/vue3/hooks/geoRecordHook';

export const useLayerTree = () => {
    // services
    const {
        getRole,
        getBuggyLocation,
        getAllAsTreeByRole,
        getBuggyTrackerDetails,
        getAllArchiveGeoRecordByEventId,
        getAllAsArchiveTreeByRole,
        getAllEventsByCourseSchemaIdAndOrgId,
    } = useService();

    // hooks
    const store = useStore();
    const { warnToast } = useNotify();
    const { toggleGeoRecord } = useGeoRecord();

    // refs
    const role = ref();
    const intervalId = ref();
    const buggySiteId = ref();
    const buggyFrequency = ref();

    const buggyData = ref<any>([]);
    const markers = ref<any>([]);

    // methods
    const toggleChildren = (
        node: any,
        map: mapboxgl.Map,
        isVisible: boolean,
        mapboxLayerState: any,
    ) => {
        node.style.metadata.children.forEach((x: any) => {
            map.setLayoutProperty(
                x,
                'visibility',
                isVisible ? 'visible' : 'none',
            );
        });
    };

    const courseNodeToggle = (
        node: any,
        map: mapboxgl.Map,
        isVisible: boolean,
        mapboxLayerState: any,
        isKeyLineView: boolean,
    ) => {
        if (node.children.length > 0) {
            node.children.forEach((childNode: any) => {
                courseNodeToggle(
                    childNode,
                    map,
                    isVisible,
                    mapboxLayerState,
                    isKeyLineView,
                );
            });
        } else {
            if (!node.data) return;

            if ((node.key === 'sat' || node.key === 'mosaic') && isKeyLineView)
                isVisible = false;

            mapboxLayerState[node.data].isVisible = isVisible;

            map.setLayoutProperty(
                node.data,
                'visibility',
                isVisible ? 'visible' : 'none',
            );

            if (!node.style || !node.style.metadata.children) return;

            toggleChildren(node, map, isVisible, mapboxLayerState);
        }

        return mapboxLayerState;
    };

    const checkMosaicAndSatellite = (selelectedNodes: any) => {
        selelectedNodes.mosaic
            ? (selelectedNodes.mosaic.checked = store.state.isKeyLineView
                  ? false
                  : true)
            : undefined;
        selelectedNodes.sat
            ? (selelectedNodes.sat.checked = store.state.isKeyLineView
                  ? false
                  : true)
            : undefined;

        return selelectedNodes;
    };

    const courseNodeSelected = (
        node: any,
        map: mapboxgl.Map,
        mapboxLayerState: any,
        selectedCourseSetupNodes: any,
        isKeyLineView: boolean,
    ) => {
        if (
            store.state.isKeyLineView &&
            (node.key === 'sat' || node.key === 'mosaic')
        ) {
            warnToast('Cannot modify selected layer in KeyLine view');
            selectedCourseSetupNodes[node.key].checked = false;
            return;
        }

        courseNodeToggle(node, map, true, mapboxLayerState, isKeyLineView);

        return {
            selectedCourseSetupNodes,
            mapboxLayerState,
        };
    };

    const courseNodeDeSelected = (
        node: any,
        map: mapboxgl.Map,
        mapboxLayerState: any,
        isKeyLineView: boolean,
    ) => {
        courseNodeToggle(node, map, false, mapboxLayerState, isKeyLineView);
        return mapboxLayerState;
    };

    const nodeGeoRecordToggle = (
        node: any,
        map: mapboxgl.Map,
        isVisible: boolean,
        geoRecordTree: any,
        mapboxLayerState: any,
    ): void => {
        toggleGeoRecord(map, node, isVisible, geoRecordTree, mapboxLayerState);
    };

    const geoRecordNodeSelected = (
        node: any,
        map: mapboxgl.Map,
        geoRecordTree: any,
        fromfilter: boolean,
        mapboxLayerState: any,
        selectedGeoRecordNodes: any,
    ) => {
        if (!fromfilter) {
            nodeGeoRecordToggle(
                node,
                map,
                true,
                geoRecordTree,
                mapboxLayerState,
            );
            if (node.children !== undefined) return;
            selectedGeoRecordNodes[node.id.toString()].checked = true;
            return;
        }

        if (selectedGeoRecordNodes[node.id.toString()] == undefined) return;

        nodeGeoRecordToggle(node, map, true, geoRecordTree, mapboxLayerState);

        if (node.children !== undefined) return;

        selectedGeoRecordNodes[node.id.toString()].checked = true;

        return selectedGeoRecordNodes;
    };

    const geoRecordNodeDeSelected = (
        node: any,
        map: mapboxgl.Map,
        geoRecordTree: any,
        fromfilter: boolean,
        mapboxLayerState: any,
        selectedGeoRecordNodes: any,
    ) => {
        if (!fromfilter) {
            nodeGeoRecordToggle(
                node,
                map,
                false,
                geoRecordTree,
                mapboxLayerState,
            );
            if (node.children !== undefined) return;
            selectedGeoRecordNodes[node.id.toString()].checked = false;
        }

        if (selectedGeoRecordNodes[node.id.toString()] === undefined) return;

        nodeGeoRecordToggle(node, map, false, geoRecordTree, mapboxLayerState);

        if (node.children !== undefined) return;

        selectedGeoRecordNodes[node.id.toString()].checked = false;

        return selectedGeoRecordNodes;
    };

    const fetchGeoRecordLayerData = async (): Promise<void> => {
        role.value = await getRole(store.getters.account.roleId);
        const catIds = role.value.roleGeoRecordCategoryPermissionsLink
            .filter((permission) => permission.canEdit || permission.canView)
            .map((permission) => permission.id);
        catIds.push(0);

        return await getAllAsTreeByRole({
            courseId: store.getters.currentCourseSchema.id,
            organizationId: store.getters.currentOrganization.id,
            catIds,
        });
    };

    const fetchGeoRecordArchiveLayerData = async (
        archiveEventId: any,
    ): Promise<void> => {
        return await getAllArchiveGeoRecordByEventId(
            store.getters.currentCourseSchema.id,
            archiveEventId,
        );
    };

    const fetchGeoRecordArchiveTreeData = async (
        archiveEventId: any,
    ): Promise<void> => {
        role.value = await getRole(store.getters.account.roleId);
        const catIds = role.value.roleGeoRecordCategoryPermissionsLink
            .filter((permission) => permission.canEdit || permission.canView)
            .map((permission) => permission.id);
        catIds.push(0);

        return await getAllAsArchiveTreeByRole({
            catIds,
            archiveEventId,
            courseSchemaId: store.getters.currentCourseSchema.id,
        });
    };

    const removeBuggyMarker = (): void => {
        markers.value.forEach((marker: any) => {
            marker.remove();
        });
        markers.value = [];
        clearInterval(intervalId.value);
    };

    const addBuggyMarker = async (map: mapboxgl.Map): Promise<void> => {
        // reset
        markers.value.forEach((marker: any) => {
            marker.remove();
        });
        markers.value = [];

        // get
        try {
            buggyData.value = await getBuggyLocation(buggySiteId.value);
            buggyData.value.forEach((x) => {
                if (x.onlineStatus === false) return;

                const marker = new mapboxgl.Marker().setLngLat(
                    x.gpsLocation.reverse(),
                );

                // if car name is not set, use car number
                if (!x.carName) {
                    x.carName = x.carNumber;
                }

                // if driver name is not set, use anonymous
                if (!x.driverName) {
                    x.driverName = 'Anonymous';
                }

                const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(
                    `<div class="flex flex-col m-2">
                    <div>Car Lock Status: ${x.carLockStatus}</div>
                    <div>Car Name: ${x.carName ? x.carname : 'Anonymous'}</div>
                    <div>Car Number: ${
                        x.carNumber ? x.carNumber : 'Anonymous'
                    }</div>
                    <div>Driver Name: ${
                        x.driverName ? x.driverName : 'Anonymous'
                    }</div>
                    <div>GPS Speed: ${x.gpsSpeed}</div>
                    <div>Status: ${x.onlineStatus ? 'Online' : 'Offline'}</div>
                    <div>Latitude: ${x.gpsLocation[0]}</div>
                    <div>Longitude: ${x.gpsLocation[1]}</div>
                </div>`,
                );

                marker.getElement().innerHTML = `<img src="${store.state.apiUrl}/assets/geo-record-icons/buggy.png" style="width: 20px; height: 20px;"/>`;

                marker.setPopup(popup);
                marker.addTo(map);
                markers.value.push(marker);
            });
        } catch (e) {
            console.error(e);
            warnToast('Error occured While adding buggy to map.');
            removeBuggyMarker();
        }
    };

    const toggleBuggyMarker = (showBuggy: Boolean, map: mapboxgl.Map): void => {
        if (!showBuggy) {
            removeBuggyMarker();
            return;
        }

        intervalId.value = setInterval(async () => {
            await addBuggyMarker(map);
        }, buggyFrequency.value * 1000);
    };

    const getBuggyConfig = async (): Promise<void> => {
        const buggyConfig = await getBuggyTrackerDetails();

        buggySiteId.value = buggyConfig.site_id;
        buggyFrequency.value = buggyConfig.frequency;
    };

    const checkBuggyTrackerPermission = (): boolean => {
        return role.value.permissions.find(
            (permission) => permission === 'canModuleBuggyTracker',
        );
    };

    const checkEuropeTourPermission = (): boolean => {
        return role.value.permissions.find(
            (permission) => permission === 'canModuleEuropeTour',
        );
    };

    const checkMeasureRecordPermission = (): boolean => {
        return role.value.permissions.find(
            (permission) => permission === 'canReadMeasureRecord',
        );
    };

    const fetchGeoRecordArchiveEvents = async (courseSchemaId: number) => {
        if (
            role.value.permissions.some((x) => x === 'canModuleArchiveEvents')
        ) {
            try {
                const archiveEvents =
                    await getAllEventsByCourseSchemaIdAndOrgId(courseSchemaId);
                return archiveEvents;
            } catch (e) {
                console.error(e);
                warnToast('Unable to fetch archive events.');
                return [];
            }
        }
    };

    const isAllowBuggyTracker = (): boolean => {
        return (
            checkBuggyTrackerPermission() &&
            buggySiteId.value !== 0 &&
            buggyFrequency.value !== 0
        );
    };

    return {
        getBuggyConfig,
        toggleBuggyMarker,
        courseNodeSelected,
        isAllowBuggyTracker,
        courseNodeDeSelected,
        geoRecordNodeSelected,
        fetchGeoRecordLayerData,
        geoRecordNodeDeSelected,
        checkMosaicAndSatellite,
        checkEuropeTourPermission,
        fetchGeoRecordArchiveLayerData,
        fetchGeoRecordArchiveTreeData,
        fetchGeoRecordArchiveEvents,
        checkMeasureRecordPermission,
    };
};
